import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'

import type { User } from '@types'

const DEFAULT_TRACES_SAMPLE_RATE = 0.1

const getTracesSampleRate = (): number => {
  const raw: string | undefined = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
  const parsed = raw ? Number(raw) : NaN
  return Number.isFinite(parsed) ? parsed : DEFAULT_TRACES_SAMPLE_RATE
}

export const initSentry = (): void => {
  const dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_TOOLKIT_ENV,
      sendDefaultPii: true,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      tracesSampleRate: getTracesSampleRate(),
    })
  } catch (error) {
    console.error(error)
  }
}

export const captureError = (error: unknown, captureContext?: Parameters<typeof Sentry.captureException>[1]): void => {
  Sentry.captureException(error, captureContext)
}

// Mirrors the authenticated user into Sentry's scope. setUser is a cheap synchronous scope
// write (no network), so callers drive *when* to call it (e.g. on identity change). Pass undefined to clear.
export const syncSentryUser = (user?: User): void => {
  Sentry.setUser(user ? { id: user.id, email: user.email, username: `${user.firstName} ${user.lastName}` } : null)
}

// Wraps react-router's createBrowserRouter so navigation transactions are named after the
// route pattern (e.g. /projects/:projectId) instead of the raw URL. No-op without a DSN.
export const sentryWrapCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7
